/**
 * ExportMenu Component
 * Dropdown menu providing multiple export options for carrier data
 */

import React, { useState, useRef, useEffect } from 'react';
import {
  Download,
  FileText,
  FileSpreadsheet,
  Image,
  Mail,
  Clock,
  ChevronDown,
  Loader,
  Check,
  AlertCircle,
} from 'lucide-react';

// Import export utilities
import {
  exportViolationsToCSV,
  exportInspectionsToCSV,
  exportCrashesToCSV,
  exportBASICScoresToCSV,
  exportCarrierProfileToCSV,
  exportFleetToCSV,
} from '../utils/csv-export';
import {
  exportViolationsToPDF,
  exportInspectionsToPDF,
  exportCrashesToPDF,
  exportBASICScoresToPDF,
  exportFleetToPDF,
  exportSummaryPDF,
} from '../utils/pdf-export';
import { generateCarrierReport } from '../utils/carrier-report-pdf';
import { exportCarrierDataToExcel, exportAnalyticsToExcel } from '../utils/excel-export';
import { exportChartAsPNG, exportRechartsChart, captureScreenshot } from '../utils/chart-export';
import type { Violation, Crash, Inspection, Vehicle, CarrierProfile, BASICScore } from '../types/api';

export type ExportDataType =
  | 'violations'
  | 'inspections'
  | 'crashes'
  | 'basics'
  | 'profile'
  | 'fleet'
  | 'all'
  | 'analytics'
  | 'chart';

export type ExportFormat = 'csv' | 'pdf' | 'excel' | 'png' | 'comprehensive';

// Re-export BASICScore for backwards compatibility
export type { BASICScore };

export interface AllCarrierData {
  profile?: CarrierProfile;
  violations?: Violation[];
  inspections?: Inspection[];
  crashes?: Crash[];
  basics?: BASICScore[];
  vehicles?: Vehicle[];
}

export interface ExportMenuProps {
  /** Type of data to export */
  dataType: ExportDataType;
  /** Data to export */
  data: Violation[] | Inspection[] | Crash[] | BASICScore[] | CarrierProfile | Vehicle[] | Record<string, unknown>;
  /** Carrier name for filename */
  carrierName?: string;
  /** DOT number */
  dotNumber?: number;
  /** All carrier data for comprehensive exports */
  allData?: AllCarrierData;
  /** Chart element ID for chart exports */
  chartElementId?: string;
  /** Custom button text */
  buttonText?: string;
  /** Button variant */
  variant?: 'primary' | 'secondary' | 'ghost';
  /** Button size */
  size?: 'sm' | 'md' | 'lg';
  /** Disabled state */
  disabled?: boolean;
  /** Available formats (if not specified, all applicable formats shown) */
  formats?: ExportFormat[];
  /** Callback when export starts */
  onExportStart?: () => void;
  /** Callback when export completes */
  onExportComplete?: (success: boolean) => void;
}

interface ExportOption {
  id: string;
  label: string;
  description: string;
  format: ExportFormat;
  icon: React.ReactNode;
}

export const ExportMenu: React.FC<ExportMenuProps> = ({
  dataType,
  data,
  carrierName,
  dotNumber,
  allData,
  chartElementId,
  buttonText = 'Export',
  variant = 'secondary',
  size = 'md',
  disabled = false,
  formats,
  onExportStart,
  onExportComplete,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);
  const [exportMessage, setExportMessage] = useState('');
  const [exportStatus, setExportStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const menuRef = useRef<HTMLDivElement>(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Get available export options based on data type
  const getExportOptions = (): ExportOption[] => {
    const options: ExportOption[] = [];

    // CSV options
    if (!formats || formats.includes('csv')) {
      if (dataType !== 'chart') {
        options.push({
          id: 'csv',
          label: 'Export as CSV',
          description: 'Comma-separated values for spreadsheets',
          format: 'csv',
          icon: <FileText size={16} />,
        });
      }
    }

    // PDF options
    if (!formats || formats.includes('pdf')) {
      if (dataType !== 'chart') {
        options.push({
          id: 'pdf',
          label: 'Export as PDF',
          description: 'Formatted document with tables',
          format: 'pdf',
          icon: <FileText size={16} />,
        });
      }
    }

    // Excel options (for all data or specific types)
    if (!formats || formats.includes('excel')) {
      if (dataType === 'all' || dataType === 'analytics') {
        options.push({
          id: 'excel',
          label: 'Export as Excel',
          description: 'Multi-sheet workbook',
          format: 'excel',
          icon: <FileSpreadsheet size={16} />,
        });
      }
    }

    // PNG options for charts
    if (!formats || formats.includes('png')) {
      if (dataType === 'chart' && chartElementId) {
        options.push({
          id: 'png',
          label: 'Export as PNG',
          description: 'High-resolution image',
          format: 'png',
          icon: <Image size={16} />,
        });
      }
    }

    // Comprehensive report (for all data)
    if (!formats || formats.includes('comprehensive')) {
      if (dataType === 'all' && allData) {
        options.push({
          id: 'comprehensive',
          label: 'Comprehensive Report',
          description: 'Multi-page professional PDF report',
          format: 'comprehensive',
          icon: <FileText size={16} />,
        });
      }
    }

    return options;
  };

  const handleExport = async (option: ExportOption) => {
    setIsExporting(true);
    setExportStatus('loading');
    setExportProgress(0);
    setExportMessage(`Generating ${option.label}...`);
    onExportStart?.();

    try {
      switch (option.format) {
        case 'csv':
          await handleCSVExport();
          break;
        case 'pdf':
          await handlePDFExport();
          break;
        case 'excel':
          await handleExcelExport();
          break;
        case 'png':
          await handlePNGExport();
          break;
        case 'comprehensive':
          await handleComprehensiveExport();
          break;
      }

      setExportStatus('success');
      setExportMessage('Export complete!');
      onExportComplete?.(true);

      // Reset after success
      setTimeout(() => {
        setIsOpen(false);
        setIsExporting(false);
        setExportStatus('idle');
        setExportMessage('');
        setExportProgress(0);
      }, 1500);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Export failed';
      console.error('Export failed:', message);
      setExportStatus('error');
      setExportMessage('Export failed. Please try again.');
      onExportComplete?.(false);

      setTimeout(() => {
        setIsExporting(false);
        setExportStatus('idle');
        setExportMessage('');
        setExportProgress(0);
      }, 3000);
    }
  };

  const handleCSVExport = async () => {
    const progressCallback = (progress: number) => setExportProgress(progress);

    switch (dataType) {
      case 'violations':
        await exportViolationsToCSV(data as any, carrierName, progressCallback);
        break;
      case 'inspections':
        await exportInspectionsToCSV(data as any, carrierName, progressCallback);
        break;
      case 'crashes':
        await exportCrashesToCSV(data as any, carrierName, progressCallback);
        break;
      case 'basics':
        await exportBASICScoresToCSV(data as BASICScore[], carrierName, progressCallback);
        break;
      case 'profile':
        await exportCarrierProfileToCSV(data as any, carrierName);
        break;
      case 'fleet':
        await exportFleetToCSV(data as any, carrierName, progressCallback);
        break;
      case 'all':
        // For "all", export as multi-sheet Excel instead
        if (allData) {
          await exportCarrierDataToExcel(allData as any, carrierName, dotNumber, progressCallback);
        }
        break;
    }
  };

  const handlePDFExport = async () => {
    const progressCallback = (progress: number) => setExportProgress(progress);

    switch (dataType) {
      case 'violations':
        await exportViolationsToPDF(data as any, carrierName, dotNumber, progressCallback);
        break;
      case 'inspections':
        await exportInspectionsToPDF(data as any, carrierName, dotNumber, progressCallback);
        break;
      case 'crashes':
        await exportCrashesToPDF(data as any, carrierName, dotNumber, progressCallback);
        break;
      case 'basics':
        await exportBASICScoresToPDF(data as BASICScore[], carrierName, dotNumber, progressCallback);
        break;
      case 'fleet':
        await exportFleetToPDF(data as any, carrierName, dotNumber, progressCallback);
        break;
      case 'all':
        if (allData) {
          await exportSummaryPDF(allData as any, carrierName, dotNumber);
        }
        break;
    }
  };

  const handleExcelExport = async () => {
    const progressCallback = (progress: number) => setExportProgress(progress);

    if (dataType === 'all' && allData) {
      await exportCarrierDataToExcel(allData as any, carrierName, dotNumber, progressCallback);
    } else if (dataType === 'analytics') {
      await exportAnalyticsToExcel(data as Record<string, unknown>, carrierName, progressCallback);
    }
  };

  const handlePNGExport = async () => {
    if (chartElementId) {
      setExportProgress(50);
      const success = await exportRechartsChart(chartElementId, `chart_${dataType}`, {
        carrierName,
        scale: 2,
      });
      setExportProgress(100);
      if (!success) {
        throw new Error('Failed to export chart');
      }
    }
  };

  const handleComprehensiveExport = async () => {
    if (allData) {
      await generateCarrierReport(
        {
          profile: allData.profile as any,
          violations: allData.violations || [],
          inspections: allData.inspections || [],
          crashes: allData.crashes || [],
          basics: allData.basics || [],
          vehicles: allData.vehicles || [],
        },
        (progress, message) => {
          setExportProgress(progress);
          setExportMessage(message);
        }
      );
    }
  };

  const exportOptions = getExportOptions();

  // If only one option, render as simple button
  if (exportOptions.length === 1) {
    return (
      <button
        onClick={() => handleExport(exportOptions[0])}
        disabled={disabled || isExporting}
        className={`btn-${variant}`}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px',
          padding: size === 'sm' ? '6px 12px' : size === 'lg' ? '12px 24px' : '8px 16px',
          fontSize: size === 'sm' ? '0.8125rem' : size === 'lg' ? '1rem' : '0.875rem',
          opacity: disabled || isExporting ? 0.6 : 1,
          cursor: disabled || isExporting ? 'not-allowed' : 'pointer',
        }}
      >
        {isExporting ? (
          <Loader size={16} className="animate-spin" />
        ) : (
          <Download size={16} />
        )}
        {isExporting ? 'Exporting...' : buttonText}
      </button>
    );
  }

  // Render dropdown menu
  return (
    <div ref={menuRef} style={{ position: 'relative', display: 'inline-block' }}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        disabled={disabled || isExporting}
        className={`btn-${variant}`}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px',
          padding: size === 'sm' ? '6px 12px' : size === 'lg' ? '12px 24px' : '8px 16px',
          fontSize: size === 'sm' ? '0.8125rem' : size === 'lg' ? '1rem' : '0.875rem',
          opacity: disabled || isExporting ? 0.6 : 1,
          cursor: disabled || isExporting ? 'not-allowed' : 'pointer',
        }}
      >
        {isExporting ? (
          <Loader size={16} style={{ animation: 'spin 1s linear infinite' }} />
        ) : (
          <Download size={16} />
        )}
        {isExporting ? 'Exporting...' : buttonText}
        <ChevronDown size={14} style={{
          transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
          transition: 'transform 0.2s',
        }} />
      </button>

      {isOpen && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            right: 0,
            marginTop: '4px',
            minWidth: '240px',
            background: '#fbf9f4',
            borderRadius: '8px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
            border: '1px solid var(--color-gray-200)',
            zIndex: "var(--z-modal)",
            overflow: 'hidden',
          }}
        >
          {isExporting ? (
            <div style={{ padding: '16px' }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                marginBottom: '12px',
              }}>
                {exportStatus === 'loading' && (
                  <Loader size={18} style={{ animation: 'spin 1s linear infinite', color: 'var(--color-primary)' }} />
                )}
                {exportStatus === 'success' && (
                  <Check size={18} style={{ color: 'var(--color-success)' }} />
                )}
                {exportStatus === 'error' && (
                  <AlertCircle size={18} style={{ color: 'var(--color-danger)' }} />
                )}
                <span style={{
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  color: exportStatus === 'error' ? 'var(--color-danger)' : 'var(--color-gray-700)',
                }}>
                  {exportMessage}
                </span>
              </div>
              {exportStatus === 'loading' && (
                <div style={{
                  width: '100%',
                  height: '6px',
                  background: 'var(--color-gray-100)',
                  borderRadius: '3px',
                  overflow: 'hidden',
                }}>
                  <div style={{
                    width: `${exportProgress}%`,
                    height: '100%',
                    background: 'var(--color-primary)',
                    borderRadius: '3px',
                    transition: 'width 0.3s ease',
                  }} />
                </div>
              )}
            </div>
          ) : (
            <>
              <div style={{
                padding: '8px 12px',
                borderBottom: '1px solid var(--color-gray-100)',
                background: 'var(--color-gray-50)',
              }}>
                <span style={{
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  color: 'var(--color-gray-500)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                }}>
                  Export Options
                </span>
              </div>
              {exportOptions.map((option) => (
                <button
                  key={option.id}
                  onClick={() => handleExport(option)}
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '12px',
                    padding: '12px 16px',
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    textAlign: 'left',
                    transition: 'background 0.2s',
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = 'var(--color-gray-50)'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                >
                  <div style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '6px',
                    background: 'var(--color-primary-light)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'var(--color-primary)',
                    flexShrink: 0,
                  }}>
                    {option.icon}
                  </div>
                  <div>
                    <div style={{
                      fontSize: '0.875rem',
                      fontWeight: 500,
                      color: 'var(--color-gray-900)',
                      marginBottom: '2px',
                    }}>
                      {option.label}
                    </div>
                    <div style={{
                      fontSize: '0.75rem',
                      color: 'var(--color-gray-500)',
                    }}>
                      {option.description}
                    </div>
                  </div>
                </button>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
};

// Simple single-format export button
export const ExportButton: React.FC<{
  onClick: () => Promise<void>;
  label?: string;
  icon?: React.ReactNode;
  variant?: 'primary' | 'secondary' | 'ghost';
  size?: 'sm' | 'md' | 'lg';
  disabled?: boolean;
}> = ({
  onClick,
  label = 'Export',
  icon = <Download size={16} />,
  variant = 'secondary',
  size = 'md',
  disabled = false,
}) => {
  const [isExporting, setIsExporting] = useState(false);

  const handleClick = async () => {
    setIsExporting(true);
    try {
      await onClick();
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <button
      onClick={handleClick}
      disabled={disabled || isExporting}
      className={`btn-${variant}`}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        padding: size === 'sm' ? '6px 12px' : size === 'lg' ? '12px 24px' : '8px 16px',
        fontSize: size === 'sm' ? '0.8125rem' : size === 'lg' ? '1rem' : '0.875rem',
        opacity: disabled || isExporting ? 0.6 : 1,
        cursor: disabled || isExporting ? 'not-allowed' : 'pointer',
      }}
    >
      {isExporting ? (
        <Loader size={16} style={{ animation: 'spin 1s linear infinite' }} />
      ) : (
        icon
      )}
      {isExporting ? 'Exporting...' : label}
    </button>
  );
};

export default ExportMenu;
